/**
 * THE AUTH SCALE — one factor that sizes the sign-in card and the two corner
 * controls on desktop (Spencer, 2026-10-09: "I think 80% zoom level looks
 * better for the log in page than 100%").
 *
 * The "80%" he was looking at was the app's own text zoom (`hooks/useTextZoom`
 * intercepts Cmd -/+ and sets `#app { zoom }`), so the target is measurable:
 * every length on the page times 0.8. Rather than ship a `zoom`/`transform`
 * on the page (which is what broke the card in the first place, see the
 * `borderBoxSize` note in SignInPage), each length the card owns is written
 * as `authPx(n)`: `calc(n px * var(--auth-scale, 1))`.
 *
 *   - The variable is UNSET by default, so `authPx(n)` is exactly `n px`
 *     everywhere that does not opt in. The shared auth chrome (fields,
 *     buttons, labels, logo, glass card) reads it, and sign-up, password
 *     setup, verify email and the 2FA screens render byte-for-byte as before
 *     unless an ancestor sets it.
 *   - Sign-in sets it on its page shell, and the corner controls set it on
 *     themselves (they live outside that shell: the toggle in
 *     LayoutAuthAmbient, Back portaled to `#app`).
 *   - It only applies on a desktop-class device: a wide viewport AND a fine
 *     pointer that can hover. A phone or a touch tablet keeps scale 1, so no
 *     tap target shrinks (fields stay 56px, buttons 48px).
 *
 * It composes with the in-app text zoom rather than fighting it: a user who
 * presses Cmd - still gets 0.9, 0.8 ... of whatever this resolves to.
 */
export const AUTH_SCALE_VAR = '--auth-scale'

/**
 * The desktop factor.
 *
 * WAS 0.8 (Spencer, 2026-10-09: "80% zoom level looks better for the log in page
 * than 100%"). Raised to 1 on 2026-10-30 against a better reference than the page
 * on its own: **the surfaces either side of it**. Measured —
 *
 *   vera.inc                     1.0  (no root font-size, no zoom anywhere in
 *                                      app/globals.css; browser default 16px)
 *   platform, signed in          1.0  (`useTextZoom` defaults to 1, user-driven)
 *   sign-up / "Access Vera"      1.0  (never opts into this variable)
 *   sign-in                      0.8  <- the only surface that was not 1
 *
 * The real journey is vera.inc -> Log in -> sign-in -> the app, so 0.8 made the
 * page shrink 20% at the hop and grow 25% back on success, with the page judged
 * in isolation being the only place that reads well. Spencer, 2026-10-30: "how is
 * the sign in page compared to vera.inc zoom level - that's what it should match
 * since you get there from login button on that page."
 *
 * KEPT AS A KNOB rather than deleted. Every `authPx(n)` is now exactly `n px`, so
 * the mechanism is inert — but it is the one lever for retuning this without
 * touching ~60 call sites, and it is still what holds sign-in and the two corner
 * controls together. If it stays at 1 indefinitely, removing it is a clean sweep.
 */
export const AUTH_DESKTOP_SCALE = 1

/**
 * Desktop-class only. 900px is MUI's `md`; `hover`/`pointer: fine` keeps it off
 * touch tablets wide enough to pass the width test, where 0.8 would take the
 * 48px buttons to 38px.
 */
export const AUTH_DESKTOP_MEDIA = '@media (min-width: 900px) and (hover: hover) and (pointer: fine)'

/** Spread into the `sx` of the element that should establish the scale. */
export const authScaleSx = {
  [AUTH_DESKTOP_MEDIA]: { [AUTH_SCALE_VAR]: AUTH_DESKTOP_SCALE },
} as const

/** A length that follows the auth scale: `n px` when nothing sets it. */
export const authPx = (px: number): string => `calc(${px}px * var(${AUTH_SCALE_VAR}, 1))`
